import { supabase } from "./client";

const APPLY_ORDER_STOCK_FUNCTION_NAME = "apply-order-stock";

const normalizeText = (value, maxLength = 240) =>
  String(value || "")
    .trim()
    .slice(0, maxLength);

const normalizeMetadata = (value) =>
  value && typeof value === "object" && !Array.isArray(value) ? value : {};

const extractFunctionErrorPayload = async (error, data) => {
  if (data && typeof data === "object") {
    return data;
  }

  const response = error?.context;

  if (!response) {
    return null;
  }

  const readableResponse =
    typeof response.clone === "function" ? response.clone() : response;

  try {
    const contentType = normalizeText(
      readableResponse.headers?.get?.("content-type"),
      120
    ).toLowerCase();

    if (contentType.includes("application/json")) {
      return await readableResponse.json();
    }

    const rawText = normalizeText(await readableResponse.text(), 500);

    if (!rawText) {
      return null;
    }

    try {
      return JSON.parse(rawText);
    } catch {
      return { error: rawText };
    }
  } catch {
    return null;
  }
};

const normalizeFunctionError = (error, data, fallbackMessage) => {
  const transportMessage = normalizeText(error?.message, 240);
  const backendMessage = normalizeText(
    data?.error || data?.message || data?.details || data?.hint,
    240
  );

  if (backendMessage && transportMessage.toLowerCase().includes("non-2xx")) {
    return backendMessage;
  }

  return backendMessage || transportMessage || fallbackMessage;
};

export const applyOperationalOrderStock = async ({
  branchId,
  orderId,
  sourceChannel = "unknown",
  sourceMode = "unknown",
  metadata = {},
} = {}) => {
  if (!supabase) {
    throw new Error(
      "Supabase no esta configurado. No podemos descontar el stock de este pedido."
    );
  }

  const body = {
    branchId: normalizeText(branchId, 120),
    orderId: normalizeText(orderId, 180),
    sourceChannel: normalizeText(sourceChannel, 80) || "unknown",
    sourceMode: normalizeText(sourceMode, 80) || "unknown",
    metadata: normalizeMetadata(metadata),
  };

  if (!body.branchId || !body.orderId) {
    return {
      ok: false,
      skipped: true,
      reason: "missing_identifiers",
    };
  }

  const { data, error } = await supabase.functions.invoke(
    APPLY_ORDER_STOCK_FUNCTION_NAME,
    { body }
  );

  if (error) {
    const errorPayload = await extractFunctionErrorPayload(error, data);

    throw new Error(
      normalizeFunctionError(
        error,
        errorPayload,
        "No pudimos descontar el stock operativo del pedido."
      )
    );
  }

  return data && typeof data === "object" ? data : { ok: true };
};
